/* eslint-disable no-console */
// BASED ON: https://github.com/google/emoji-scavenger-hunt

export const AUDIO = {
  CLOCK_END: 'clockEnd',
  CLOCK_TICKING: 'clockTicking',
  VICTORY: 'victory',
  CORRECT: 'correct',
  WRONG: 'wrong'
};

export default class AudioPlayer {
  sources;
  volumes;

  constructor() {
    this.sources = {
      [AUDIO.CLOCK_END]: new Audio('/sounds/clock-end.mp3'),
      [AUDIO.CLOCK_TICKING]: new Audio('/sounds/clock.mp3'),
      [AUDIO.VICTORY]: new Audio('/sounds/victory.mp3'),
      [AUDIO.CORRECT]: new Audio('/sounds/correct.mp3'),
      [AUDIO.WRONG]: new Audio('/sounds/wrong.mp3')
    };
    // change default global volume settings here
    this.volumes = {
      [AUDIO.CLOCK_END]: 0.025,
      [AUDIO.CLOCK_TICKING]: 0.2,
      [AUDIO.WRONG]: 0.3
    };
  }

  /**
   * Cycles audio sources for the game and plays them and immediately pausing
   * them after. This ensures they are ready for play later on during the game
   * lifecycle and can be played from JavaScript functions.
   */
  setup() {
    // We need to start and pause the audio sources when the game is
    // initialized since we can't play audio sources from JS on mobile
    // when not initiated from a user action (like a click event).
    for (const item of Object.keys(this.sources)) {
      this.sources[item].muted = true;
      let playPromise = this.sources[item].play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this.sources[item].pause();
            this.sources[item].muted = false;
          })
          .catch(error => {
            console.error(error);
          });
      }
    }
  }

  muteAll() {
    this._setMuteStatus(true);
  }

  unmuteAll() {
    this._setMuteStatus(false);
  }

  _setMuteStatus(status = false) {
    for (const item of Object.keys(this.sources)) {
      this.sources[item].muted = status;
    }
  }

  /**
   * Resets all audio sources.
   */
  reset() {
    for (const item of Object.keys(this.sources)) {
      this.pause(item);
    }
  }

  /**
   * Plays a provided audio file.
   * @param audio The audio file to play.
   * @param loop Indicates if the audio file should loop.
   */
  play(audio, settings = {}) {
    const { volume = 1, loop = false, startTime = 0, endTime } = settings;
    let audioElement = this.sources[audio];
    if (loop) {
      audioElement.loop = true;
    }
    // set volume
    audioElement.volume = this.volumes[audio] || volume;
    audioElement.currentTime = startTime;
    let playPromise = audioElement.play();

    if (endTime !== undefined) {
      const timeUpdate = () => {
        if (audioElement.currentTime >= endTime) {
          audioElement.pause();
          audioElement.removeEventListener('timeupdate', timeUpdate);
        }
      };

      audioElement.addEventListener('timeupdate', timeUpdate);
    }

    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.error(error);
      });
    }
  }

  /**
   * Pauses an audio file.
   * @param audio The audio file to pause.
   */
  pause(audio) {
    this.sources[audio].pause();
    this.sources[audio].currentTime = 0;
  }

  /**
   * Checks if the provided audio file is currently playing.
   * @param audio The audio file to test against.
   * @returns true if the audio is playing, false if not.
   */
  isPlaying(audio) {
    return !this.sources[audio].paused;
  }
}
